import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDeleteAccount } from '../../api/hooks/useUser';
import { Button } from '../actions/Button';
import { TextInput } from '../inputs/TextInput';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DeleteAccountModal({ isOpen, onClose }: DeleteAccountModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const deleteAccount = useDeleteAccount();
  const navigate = useNavigate();

  const canDelete = confirmText === 'DELETE';

  const handleDelete = async () => {
    if (!canDelete) return;

    try {
      await deleteAccount.mutateAsync();
      navigate('/login');
    } catch (err) {
      setError('Failed to delete account. Please try again.');
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setError('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-background-card rounded-xl shadow-xl z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-error" />
                </div>
                <h2 className="text-lg font-semibold text-text-primary">
                  Delete Account
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full hover:bg-background flex items-center justify-center text-text-secondary"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-text-secondary">
                This action is <strong className="text-error">permanent</strong> and cannot be undone.
                All your data will be deleted, including:
              </p>
              <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                <li>Your profile and preferences</li>
                <li>All saved content</li>
                <li>Subscription and billing history</li>
                <li>All account data</li>
              </ul>

              <div className="pt-2">
                <TextInput
                  label='Type "DELETE" to confirm'
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="DELETE"
                  error={error}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t border-border bg-background">
              <Button
                variant="secondary"
                onClick={handleClose}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDelete}
                disabled={!canDelete || deleteAccount.isPending}
                fullWidth
                className="!bg-error hover:!bg-error/90"
              >
                {deleteAccount.isPending ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
